import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { StartupScreen } from "@/components/startup-screen";

import { useAuth } from "./auth-context";

/**
 * Gate for everything behind sign-in.
 *
 * Waits out `loading` instead of redirecting, so a returning breeder is never bounced to
 * the login screen while their stored token is still being checked. The attempted
 * location is handed to the login screen so they land where they were going.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <StartupScreen />;
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Gate for the aviary itself: everything a breeder is waiting to be let into.
 *
 * Sits inside {@link RequireAuth}, so by the time it runs the breeder is signed in and
 * their standing is known. The API refuses these screens' data independently — this only
 * saves them a wall of failed requests they can do nothing about.
 */
export function RequireApproved({ children }: { children?: ReactNode }) {
  const { isApproved } = useAuth();

  if (!isApproved) {
    return <Navigate to="/awaiting-approval" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Gate for the administration screens.
 *
 * Sends a breeder who is not an administrator home rather than to a refusal. They did not
 * do anything wrong — they followed a link that was never meant for them, and a page
 * explaining that they lack a role they have never heard of is worse than the dashboard.
 */
export function RequireAdmin({ children }: { children?: ReactNode }) {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Gate for the sign-in and register screens. A breeder who is already signed in has no
 * business on them — following a stale bookmark should land them in the app, not on a
 * login form that immediately looks broken.
 */
export function RequireAnonymous({ children }: { children?: ReactNode }) {
  const { status } = useAuth();

  if (status === "loading") {
    return <StartupScreen />;
  }

  if (status === "authenticated") {
    return <Navigate to="/" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
